import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  Eye,
  Users,
  TrendingUp,
  ExternalLink,
  Rocket,
  Clock,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  Music2,
  AtSign,
  LogOut,
  Film,
  User,
  Repeat2
} from 'lucide-react';
import Badge from '../ui/Badge';

interface StoryMetrics {
  views: number;
  reach: number;
  replies: number;
  exits: number;
  taps_forward: number;
  taps_back: number;
  navigation?: number;
  total_interactions?: number;
  shares?: number;
  profile_visits?: number;
  follows?: number;
}

interface PostMetrics {
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  reach: number;
  impressions: number;
  engagement_rate: number;
  story?: StoryMetrics;
}

interface PublishedPost {
  id: string;
  content: string;
  platforms: string[];
  published_at: string;
  media_urls?: string[];
  external_ids?: Record<string, string>;
  metrics: PostMetrics;
  permalink?: string;
  account_username?: string;
  account_profile_image?: string;
  content_type?: string;
}

interface PostMetricsCardProps {
  post: PublishedPost;
  onBoost?: () => void;
}

const PostMetricsCard: React.FC<PostMetricsCardProps> = ({ post, onBoost }) => {
  const { t } = useTranslation();

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, React.ReactNode> = {
      instagram: <Instagram className="w-4 h-4" />,
      facebook: <Facebook className="w-4 h-4" />,
      linkedin: <Linkedin className="w-4 h-4" />,
      twitter: <Twitter className="w-4 h-4" />,
      youtube: <Youtube className="w-4 h-4" />,
      tiktok: <Music2 className="w-4 h-4" />,
      threads: <AtSign className="w-4 h-4" />
    };
    return icons[platform] || null;
  };

  const getPlatformColor = (platform: string) => {
    const colors: Record<string, string> = {
      instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
      facebook: 'bg-blue-600',
      linkedin: 'bg-blue-700',
      twitter: 'bg-sky-500',
      tiktok: 'bg-black',
      youtube: 'bg-red-600',
      threads: 'bg-black'
    };
    return colors[platform] || 'bg-gray-500';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString('pt-BR');
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExternalUrl = (platform: string, externalId: string) => {
    if (!externalId) return null;

    const urls: Record<string, string> = {
      instagram: `https://www.instagram.com/p/${externalId}/`,
      facebook: `https://www.facebook.com/${externalId}`,
      linkedin: `https://www.linkedin.com/feed/update/${externalId}`,
      twitter: `https://twitter.com/i/status/${externalId}`,
      tiktok: `https://www.tiktok.com/@user/video/${externalId}`,
      youtube: `https://www.youtube.com/watch?v=${externalId}`,
      threads: `https://www.threads.net/post/${externalId}`
    };

    return urls[platform] || null;
  };

  const metrics = post.metrics || {
    likes: 0,
    comments: 0,
    shares: 0,
    saves: 0,
    reach: 0,
    impressions: 0,
    engagement_rate: 0
  };

  // Get the link to view the post (use permalink if available, otherwise construct from external_ids)
  const getPostLink = () => {
    if (post.permalink) return post.permalink;

    const platform = post.platforms?.[0];
    const externalId = post.external_ids?.[platform];
    if (platform && externalId) {
      return getExternalUrl(platform, externalId);
    }
    return null;
  };

  const postLink = getPostLink();

  const hasMedia = post.media_urls && post.media_urls.length > 0 && post.media_urls[0];
  const hasContent = post.content && post.content.trim().length > 0;
  const platform = post.platforms?.[0] || 'unknown';
  const [imageError, setImageError] = React.useState(false);

  // Stories mostram interações próprias (views/alcance/respostas/saídas/toques),
  // não likes/saves. Detecta pelo content_type ou pela presença de métricas de story.
  const isStory = post.content_type === 'story' || !!metrics.story;
  const story = metrics.story;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-lg transition-all flex flex-col">
      {/* Header with platform and account */}
      <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 ${getPlatformColor(platform)} rounded-full flex items-center justify-center text-white shadow-sm`}
          >
            {getPlatformIcon(platform)}
          </div>
          <div>
            <span className="text-sm font-medium text-gray-900 dark:text-white capitalize inline-flex items-center gap-1.5">
              {platform}
              {isStory && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-1.5 py-0.5 rounded">
                  <Film className="w-3 h-3" /> {t('socialMetrics.story', 'Story')}
                </span>
              )}
            </span>
            {post.account_username && (
              <span className="text-xs text-gray-500 dark:text-gray-400 block">
                @{post.account_username}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          <Clock className="w-3 h-3" />
          {formatDate(post.published_at)}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1">
        {hasMedia && !imageError ? (
          <div className="relative">
            <img
              src={post.media_urls![0]}
              alt="Post media"
              className="w-full h-40 object-cover bg-gray-100 dark:bg-gray-700"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={() => setImageError(true)}
              loading="lazy"
            />
            {/* Content overlay on image */}
            {hasContent && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-xs text-white line-clamp-2">
                  {post.content}
                </p>
              </div>
            )}
          </div>
        ) : hasContent ? (
          <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/50 dark:to-gray-800/50 min-h-[100px]">
            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-4">
              {post.content}
            </p>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-center h-24">
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
              {t('socialMetrics.noContent', 'Sem conteúdo de texto')}
            </p>
          </div>
        )}
      </div>

      {/* Metrics Section */}
      <div className="p-3 border-t border-gray-100 dark:border-gray-700">
        {isStory ? (
          /* Story interactions: views, reach, replies, exits, taps */
          <>
            <div className="flex items-center gap-4 mb-2">
              <span className="flex items-center gap-1 text-sm" title={t('socialMetrics.views', 'visualizações') as string}>
                <Eye className="w-4 h-4 text-purple-500" />
                <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(story?.views || 0)}</span>
              </span>
              <span className="flex items-center gap-1 text-sm" title={t('socialMetrics.reached', 'alcance') as string}>
                <Users className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(story?.reach || 0)}</span>
              </span>
              <span className="flex items-center gap-1 text-sm" title={t('socialMetrics.replies', 'respostas') as string}>
                <MessageCircle className="w-4 h-4 text-green-500" />
                <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(story?.replies || 0)}</span>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
              <span className="flex items-center gap-1" title={t('socialMetrics.shares', 'compartilhamentos') as string}>
                <Repeat2 className="w-3 h-3" />{formatNumber(story?.shares || 0)} {t('socialMetrics.shares', 'compart.')}
              </span>
              <span className="flex items-center gap-1" title={t('socialMetrics.profileVisits', 'visitas ao perfil') as string}>
                <User className="w-3 h-3" />{formatNumber(story?.profile_visits || 0)} {t('socialMetrics.profileVisits', 'visitas')}
              </span>
              <span className="flex items-center gap-1" title={t('socialMetrics.exits', 'saídas') as string}>
                <LogOut className="w-3 h-3" />{formatNumber(story?.exits || 0)} {t('socialMetrics.exits', 'saídas')}
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Main metrics row */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1 text-sm">
                  <Heart className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(metrics.likes)}</span>
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <MessageCircle className="w-4 h-4 text-blue-500" />
                  <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(metrics.comments)}</span>
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <Share2 className="w-4 h-4 text-green-500" />
                  <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(metrics.shares)}</span>
                </span>
              </div>
              {metrics.engagement_rate > 0 && (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                  <TrendingUp className="w-3 h-3" />
                  {metrics.engagement_rate.toFixed(2)}%
                </span>
              )}
            </div>

            {/* Secondary metrics */}
            {(metrics.impressions > 0 || metrics.reach > 0 || metrics.saves > 0) && (
              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
                {metrics.impressions > 0 && (
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {formatNumber(metrics.impressions)} {t('socialMetrics.views', 'views')}
                  </span>
                )}
                {metrics.reach > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {formatNumber(metrics.reach)} {t('socialMetrics.reached', 'alcance')}
                  </span>
                )}
                {metrics.saves > 0 && (
                  <span className="flex items-center gap-1">
                    <Bookmark className="w-3 h-3" />
                    {formatNumber(metrics.saves)}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {postLink && (
            <a
              href={postLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 flex-1 py-2 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors border border-primary-200 dark:border-primary-800"
            >
              <ExternalLink className="w-3 h-3" />
              {t('socialMetrics.viewOnPlatform', 'Ver na plataforma')}
            </a>
          )}
          {onBoost && (platform === 'facebook' || platform === 'instagram') && (
            <button
              onClick={onBoost}
              className="flex items-center justify-center gap-2 flex-1 py-2 text-xs text-white bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 rounded-lg transition-colors"
            >
              <Rocket className="w-3 h-3" />
              Impulsionar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostMetricsCard;
